import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getConsultationPath } from "../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../constants/webLeadModules";
import "./Hero.css";

/* ════════════════════════════════════════════════════
   SCENE 1 — Portfolio Management
════════════════════════════════════════════════════ */
const PortfolioScene = () => (
  <div className="wc-ui-scene-wrapper wc-ui-scene-fade-in">
    <div className="wc-ui-badge-pill wc-ui-badge-tl">
      <i className="fas fa-briefcase" />
      <span>₹100cr+ AUM</span>
    </div>
    <div className="wc-ui-coin-3d wc-ui-coin-tr">
      <div className="wc-ui-coin-face wc-ui-coin-front">₹</div>
      <div className="wc-ui-coin-face wc-ui-coin-edge" />
    </div>
    <div className="wc-ui-main-card wc-ui-card-light">
      <div className="wc-ui-card-titlebar">
        <div className="wc-ui-titlebar-dots">
          <span />
          <span />
          <span />
        </div>
        <span className="wc-ui-card-title-text">Portfolio Overview</span>
        <div className="wc-ui-badge-chip wc-ui-chip-green">+57%</div>
      </div>
      <div className="wc-ui-chart-area">
        <div className="wc-ui-chart-y-labels">
          <span>₹80L</span>
          <span>₹60L</span>
          <span>₹40L</span>
          <span>₹20L</span>
          <span>₹0</span>
        </div>
        <div className="wc-ui-chart-body">
          <svg
            viewBox="0 0 260 120"
            className="wc-ui-chart-svg"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="wc-area-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2563eb" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#2563eb" stopOpacity="0.02" />
              </linearGradient>
              <linearGradient id="wc-line-grad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#2563eb" />
                <stop offset="100%" stopColor="#10b981" />
              </linearGradient>
            </defs>
            {[20, 45, 70, 95].map((y, i) => (
              <line
                key={i}
                x1="0"
                y1={y}
                x2="260"
                y2={y}
                stroke="rgba(37,99,235,0.07)"
                strokeWidth="1"
              />
            ))}
            <path
              d="M0,115 C30,108 50,100 70,90 C90,80 110,70 130,58 C150,46 170,36 195,24 C215,15 240,10 260,6 L260,120 L0,120 Z"
              fill="url(#wc-area-grad)"
              className="wc-ui-chart-area-fill"
            />
            <path
              d="M0,115 C30,108 50,100 70,90 C90,80 110,70 130,58 C150,46 170,36 195,24 C215,15 240,10 260,6"
              fill="none"
              stroke="url(#wc-line-grad)"
              strokeWidth="3"
              strokeLinecap="round"
              className="wc-ui-chart-line-path"
            />
            {[
              [0, 115],
              [70, 90],
              [130, 58],
              [195, 24],
              [260, 6],
            ].map(([cx, cy], i) => (
              <circle
                key={i}
                cx={cx}
                cy={cy}
                r="4"
                fill="white"
                stroke="#2563eb"
                strokeWidth="2"
                className="wc-ui-chart-dot"
                style={{ animationDelay: `${i * 0.15 + 1}s` }}
              />
            ))}
          </svg>
          <div className="wc-ui-chart-x-labels">
            <span>Jan</span>
            <span>Mar</span>
            <span>Jun</span>
            <span>Sep</span>
            <span>Dec</span>
          </div>
        </div>
      </div>
    </div>
    <div className="wc-ui-money-stack-3d wc-ui-stack-bl">
      <div className="wc-ui-bill wc-ui-bill-1">₹</div>
      <div className="wc-ui-bill wc-ui-bill-2">₹</div>
      <div className="wc-ui-bill wc-ui-bill-3">₹</div>
    </div>
    <div className="wc-ui-badge-pill wc-ui-badge-br">
      <i className="fas fa-users" />
      <span>800+ Investors</span>
    </div>
    <div className="wc-ui-icon-orb wc-ui-orb-shield wc-ui-orb-ml">
      <i className="fas fa-shield-halved" />
    </div>
  </div>
);

/* ════════════════════════════════════════════════════
   SCENE 2 — Wealth Planning
════════════════════════════════════════════════════ */
const WealthPlanningScene = () => (
  <div className="wc-ui-scene-wrapper wc-ui-scene-fade-in">
    <div className="wc-ui-badge-pill wc-ui-badge-tl">
      <i className="fas fa-bullseye" />
      <span>Goal-Based</span>
    </div>
    <div className="wc-ui-icon-orb wc-ui-orb-cyan wc-ui-orb-tr">
      <i className="fas fa-chart-pie" />
    </div>
    <div className="wc-ui-main-card wc-ui-card-light">
      <div className="wc-ui-card-titlebar">
        <div className="wc-ui-titlebar-dots">
          <span />
          <span />
          <span />
        </div>
        <span className="wc-ui-card-title-text">Wealth Roadmap</span>
        <div className="wc-ui-badge-chip wc-ui-chip-cyan">On Track</div>
      </div>
      <div className="wc-ui-roadmap">
        {[
          { year: "2025", goal: "Emergency Fund", done: true, amount: "₹5L" },
          { year: "2027", goal: "Home Purchase", done: true, amount: "₹40L" },
          {
            year: "2030",
            goal: "Child's Education",
            done: false,
            amount: "₹15L",
          },
          {
            year: "2040",
            goal: "Retirement Corpus",
            done: false,
            amount: "₹2.5Cr",
          },
        ].map((m, i) => (
          <div
            className="wc-ui-milestone"
            key={i}
            style={{ animationDelay: `${i * 0.12 + 0.3}s` }}
          >
            <div
              className={`wc-ui-milestone-dot${m.done ? " wc-ui-done" : ""}`}
            >
              {m.done && <i className="fas fa-check" />}
            </div>
            <div className="wc-ui-milestone-info">
              <span className="wc-ui-milestone-year">{m.year}</span>
              <span className="wc-ui-milestone-goal">{m.goal}</span>
            </div>
            <span className="wc-ui-milestone-amt">{m.amount}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="wc-ui-float-card wc-ui-fc-bl">
      <i className="fas fa-layer-group" />
      <span>Asset Mix</span>
    </div>
    <div className="wc-ui-badge-pill wc-ui-badge-br">
      <i className="fas fa-seedling" />
      <span>10yr Plan</span>
    </div>
  </div>
);

/* ════════════════════════════════════════════════════
   SCENE 3 — Retirement Advisory
════════════════════════════════════════════════════ */
const RetirementScene = () => (
  <div className="wc-ui-scene-wrapper wc-ui-scene-fade-in">
    <div className="wc-ui-badge-pill wc-ui-badge-tl">
      <i className="fas fa-calendar-check" />
      <span>SIP Active</span>
    </div>
    <div className="wc-ui-icon-orb wc-ui-orb-purple wc-ui-orb-tr">
      <i className="fas fa-umbrella" />
    </div>
    <div className="wc-ui-main-card wc-ui-card-purple">
      <div className="wc-ui-card-titlebar">
        <div className="wc-ui-titlebar-dots">
          <span />
          <span />
          <span />
        </div>
        <span className="wc-ui-card-title-text wc-ui-label-light">
          Retirement Corpus
        </span>
        <div className="wc-ui-badge-chip wc-ui-chip-light">2040</div>
      </div>
      <div className="wc-ui-corpus-display">
        <div className="wc-ui-corpus-ring">
          <svg viewBox="0 0 120 120" className="wc-ui-ring-svg">
            <circle
              cx="60"
              cy="60"
              r="50"
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="10"
            />
            <circle
              cx="60"
              cy="60"
              r="50"
              fill="none"
              stroke="url(#wc-ring-grad)"
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray="314"
              strokeDashoffset="94"
              className="wc-ui-ring-progress"
              transform="rotate(-90 60 60)"
            />
            <defs>
              <linearGradient id="wc-ring-grad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#a78bfa" />
                <stop offset="100%" stopColor="#34d399" />
              </linearGradient>
            </defs>
          </svg>
          <div className="wc-ui-ring-center">
            <span className="wc-ui-ring-pct">70%</span>
            <span className="wc-ui-ring-sub">funded</span>
          </div>
        </div>
        <div className="wc-ui-corpus-stats">
          <div className="wc-ui-corpus-row">
            <span>Target</span>
            <span className="wc-ui-corpus-val">₹2.5 Cr</span>
          </div>
          <div className="wc-ui-corpus-row">
            <span>Accumulated</span>
            <span className="wc-ui-corpus-val wc-ui-val-green">₹1.75 Cr</span>
          </div>
          <div className="wc-ui-corpus-row">
            <span>Monthly SIP</span>
            <span className="wc-ui-corpus-val">₹25,000</span>
          </div>
        </div>
      </div>
    </div>
    <div className="wc-ui-float-card wc-ui-fc-bl">
      <i className="fas fa-landmark" />
      <span>NPS</span>
    </div>
    <div className="wc-ui-badge-pill wc-ui-badge-br">
      <i className="fas fa-coins" />
      <span>₹80K/mo</span>
    </div>
  </div>
);

/* ════════════════════════════════════════════════════
   SCENE 4 — Loan Against Securities
════════════════════════════════════════════════════ */
const LoanScene = () => (
  <div className="wc-ui-scene-wrapper wc-ui-scene-fade-in">
    <div className="wc-ui-sec-doc-stack">
      <div className="wc-ui-sec-doc-card">
        <i className="fas fa-file-invoice" />
        <span>Securities</span>
      </div>
      <div className="wc-ui-sec-doc-card" style={{ animationDelay: "0.3s" }}>
        <i className="fas fa-certificate" />
        <span>Bonds</span>
      </div>
    </div>
    <div className="wc-ui-credit-card-3d wc-ui-cc-tr">
      <div className="wc-ui-cc-chip" />
      <div className="wc-ui-cc-dots">•••• •••• •••• 1234</div>
    </div>
    <div className="wc-ui-main-card wc-ui-card-dark-blue">
      <div className="wc-ui-loan-header-s6">
        <i className="fas fa-building-columns" />
        <span>Loan Against Securities</span>
      </div>
      <div className="wc-ui-loan-credit-box">
        <span className="wc-ui-loan-credit-label">Available Credit</span>
        <span className="wc-ui-loan-credit-val">₹50 Lakhs</span>
      </div>
      <div className="wc-ui-loan-meta">
        <div className="wc-ui-loan-row-s6">
          <span>Interest Rate</span>
          <span className="wc-ui-loan-highlight">8.5% p.a.</span>
        </div>
        <div className="wc-ui-loan-row-s6">
          <span>Approval Time</span>
          <span className="wc-ui-loan-highlight">24 Hours</span>
        </div>
      </div>
    </div>
    <div className="wc-ui-approval wc-ui-approval-bl">
      <i className="fas fa-circle-check" />
      <span>APPROVED</span>
    </div>
    <div className="wc-ui-money-stack-3d wc-ui-stack-br-loan">
      <div className="wc-ui-bill wc-ui-bill-1">₹</div>
      <div className="wc-ui-bill wc-ui-bill-2">₹</div>
      <div className="wc-ui-bill wc-ui-bill-3">₹</div>
    </div>
    <div className="wc-ui-icon-orb wc-ui-orb-green wc-ui-orb-mr-loan">
      <i className="fas fa-lock" />
    </div>
  </div>
);

/* ════════════════════════════════════════════════════
   SCENE 5 — Tax-Efficient Investing
════════════════════════════════════════════════════ */
const TaxScene = () => (
  <div className="wc-ui-scene-wrapper wc-ui-scene-fade-in">
    <div className="wc-ui-badge-pill wc-ui-badge-tl">
      <i className="fas fa-leaf" />
      <span>ELSS Eligible</span>
    </div>
    <div className="wc-ui-icon-orb wc-ui-orb-amber wc-ui-orb-tr">
      <i className="fas fa-piggy-bank" />
    </div>
    <div className="wc-ui-main-card wc-ui-card-light">
      <div className="wc-ui-card-titlebar">
        <div className="wc-ui-titlebar-dots">
          <span />
          <span />
          <span />
        </div>
        <span className="wc-ui-card-title-text">Tax Dashboard</span>
        <div className="wc-ui-badge-chip wc-ui-chip-amber">FY 2024-25</div>
      </div>
      <div className="wc-ui-tax-grid">
        {[
          {
            lbl: "80C Savings",
            val: "₹1.5L",
            w: "100%",
            bg: "linear-gradient(90deg,#f59e0b,#fbbf24)",
            hi: true,
            green: false,
          },
          {
            lbl: "LTCG Harvested",
            val: "₹38,000",
            w: "63%",
            bg: "linear-gradient(90deg,#2563eb,#60a5fa)",
            hi: false,
            green: false,
          },
          {
            lbl: "Net Tax Saved",
            val: "₹46,800",
            w: "78%",
            bg: "linear-gradient(90deg,#10b981,#34d399)",
            hi: false,
            green: true,
          },
          {
            lbl: "HUF Benefit",
            val: "₹12,500",
            w: "42%",
            bg: "linear-gradient(90deg,#7c3aed,#a78bfa)",
            hi: false,
            green: false,
          },
        ].map((c, i) => (
          <div
            key={i}
            className={`wc-ui-tax-cell${c.hi ? " wc-ui-tax-highlight" : ""}`}
          >
            <span className="wc-ui-tax-lbl">{c.lbl}</span>
            <span
              className={`wc-ui-tax-val${c.green ? " wc-ui-val-green" : ""}`}
            >
              {c.val}
            </span>
            <div className="wc-ui-tax-bar">
              <div
                className="wc-ui-tax-fill"
                style={{ width: c.w, background: c.bg }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="wc-ui-float-card wc-ui-fc-bl">
      <i className="fas fa-file-invoice-dollar" />
      <span>ITR Filed</span>
    </div>
    <div className="wc-ui-badge-pill wc-ui-badge-br">
      <i className="fas fa-percent" />
      <span>30% Saved</span>
    </div>
  </div>
);

/* ════════════════════════════════════════════════════
   SCENE 6 — Risk Management
════════════════════════════════════════════════════ */
const RiskScene = () => (
  <div className="wc-ui-scene-wrapper wc-ui-scene-fade-in">
    <div className="wc-ui-badge-pill wc-ui-badge-tl">
      <i className="fas fa-gauge-high" />
      <span>Risk: Low</span>
    </div>
    <div className="wc-ui-icon-orb wc-ui-orb-red wc-ui-orb-tr">
      <i className="fas fa-shield-halved" />
    </div>
    <div className="wc-ui-main-card wc-ui-card-dark">
      <div className="wc-ui-card-titlebar">
        <div className="wc-ui-titlebar-dots">
          <span />
          <span />
          <span />
        </div>
        <span className="wc-ui-card-title-text wc-ui-label-light">
          Risk Monitor
        </span>
        <span className="wc-ui-status-dot" />
      </div>
      <div className="wc-ui-risk-meters">
        {[
          { label: "Market Risk", pct: 28, color: "#10b981" },
          { label: "Sector Risk", pct: 42, color: "#f59e0b" },
          { label: "Currency Risk", pct: 15, color: "#10b981" },
          { label: "Liquidity", pct: 72, color: "#ef4444" },
        ].map((r, i) => (
          <div
            className="wc-ui-risk-row"
            key={i}
            style={{ animationDelay: `${i * 0.1 + 0.4}s` }}
          >
            <span className="wc-ui-risk-lbl">{r.label}</span>
            <div className="wc-ui-risk-track">
              <div
                className="wc-ui-risk-fill"
                style={{
                  width: `${r.pct}%`,
                  background: r.color,
                  boxShadow: `0 0 8px ${r.color}80`,
                  transitionDelay: `${i * 0.1 + 0.5}s`,
                }}
              />
            </div>
            <span className="wc-ui-risk-pct" style={{ color: r.color }}>
              {r.pct}%
            </span>
          </div>
        ))}
      </div>
      <div className="wc-ui-risk-alert">
        <i className="fas fa-circle-check" style={{ color: "#10b981" }} />
        <span>Portfolio well-diversified</span>
      </div>
    </div>
    <div className="wc-ui-float-card wc-ui-fc-bl">
      <i className="fas fa-arrows-split-up-and-left" />
      <span>Hedged</span>
    </div>
    <div className="wc-ui-badge-pill wc-ui-badge-br">
      <i className="fas fa-lock" />
      <span>Protected</span>
    </div>
  </div>
);

/* ── Scene registry ── */
const SCENES = [
  {
    id: 0,
    label: "Portfolio",
    icon: "fa-briefcase",
    Component: PortfolioScene,
  },
  {
    id: 1,
    label: "Wealth",
    icon: "fa-chart-pie",
    Component: WealthPlanningScene,
  },
  {
    id: 2,
    label: "Retirement",
    icon: "fa-umbrella",
    Component: RetirementScene,
  },
  { id: 3, label: "Loan", icon: "fa-building-columns", Component: LoanScene },
  { id: 4, label: "Tax", icon: "fa-file-invoice-dollar", Component: TaxScene },
  { id: 5, label: "Risk", icon: "fa-shield-halved", Component: RiskScene },
];

/* ════════════════════════════════════════════════════
   HERO COMPONENT
════════════════════════════════════════════════════ */
const Hero = () => {
  const [currentScene, setCurrentScene] = useState(0);

  /* Hide floating icons when hero scrolls out of view */
  useEffect(() => {
    const hero = document.getElementById("wc-ui-hero-section");
    if (!hero) return;
    const obs = new IntersectionObserver(
      ([entry]) =>
        document.body.classList.toggle(
          "wc-ui-hide-icons",
          !entry.isIntersecting,
        ),
      { threshold: 0.2 },
    );
    obs.observe(hero);
    return () => obs.disconnect();
  }, []);

  /* Auto-rotate every 5 s */
  useEffect(() => {
    const t = setInterval(
      () => setCurrentScene((p) => (p + 1) % SCENES.length),
      5000,
    );
    return () => clearInterval(t);
  }, []);

  const ActiveScene = SCENES[currentScene].Component;
  const navigate = useNavigate();

  const handleConsultationClick = (e) => {
    e.preventDefault();
    navigate(getConsultationPath(WEB_LEAD_MODULES.HOME));
  };

  return (
    <section className="wc-ui-hero-section" id="wc-ui-hero-section">
      <div className="wc-ui-hero-container">
        <div className="wc-ui-hero-content">
          {/* ── Left: text col ── */}
          <div className="wc-ui-hero-text">
            <div className="wc-ui-hero-badge">
              <span className="wc-ui-hero-badge-icon">📊</span>
              <span>Trusted by 800+ Investors</span>
            </div>
            <h1 className="wc-ui-hero-title">
              Build Long-Term Wealth
              <br />
              <span className="wc-ui-gradient-text">
                with the Power of Compounding
              </span>
            </h1>
            <p className="wc-ui-hero-subtitle">
              At Wealth Compounders, we believe that while you work hard for money, your money should work even harder for you.
            </p>
            <div className="wc-ui-hero-actions">
              <a
                href={getConsultationPath(WEB_LEAD_MODULES.HOME)}
                className="wc-ui-hero-btn wc-ui-hero-btn-primary"
                onClick={handleConsultationClick}
              >
                Book a Free Portfolio Review
                <i className="fas fa-sparkles" />
              </a>
            </div>

            {/* <div className="wc-ui-hero-stats">
              <div className="wc-ui-hero-stat">
                <span className="wc-ui-hero-stat-value">₹100cr+</span>
                <span className="wc-ui-hero-stat-label">Assets Managed</span>
              </div>
              <div className="wc-ui-hero-stat">
                <span className="wc-ui-hero-stat-value">99%</span>
                <span className="wc-ui-hero-stat-label">
                  client satisfaction
                </span>
              </div>
              <div className="wc-ui-hero-stat">
                <span className="wc-ui-hero-stat-value">20+</span>
                <span className="wc-ui-hero-stat-label">Years Excellence</span>
              </div>
            </div> */}
          </div>

          {/* ── Right: isometric visual col ── */}
          <div className="wc-ui-hero-visual">
            <div className="wc-ui-iso-scene">
              {/* key forces full remount + re-animation on scene change */}
              <ActiveScene key={currentScene} />
            </div>

            {/* Tab selector — replaces the 4-dot indicators */}
            <div className="wc-ui-scene-tabs">
              {SCENES.map((s) => (
                <button
                  key={s.id}
                  className={`wc-ui-scene-tab${currentScene === s.id ? " wc-ui-tab-active" : ""}`}
                  onClick={() => setCurrentScene(s.id)}
                  title={s.label}
                >
                  <i className={`fas ${s.icon}`} />
                  <span>{s.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
